import { vec4 } from 'gl-matrix'
import gsap from 'gsap'
import { GLContext } from 'nanogl/types'
import Node from 'nanogl-gltf/lib/elements/Node'
import MaterialOverrideExtension from 'nanogl-gltf/lib/extensions/MaterialOverrideExtension'
import Gltf from 'nanogl-gltf/lib/Gltf'
import Chunk from 'nanogl-pbr/Chunk'
import ChunksSlots from 'nanogl-pbr/ChunksSlots'
import Input, { ShaderType, Uniform } from 'nanogl-pbr/Input'
import TexCoord from 'nanogl-pbr/TexCoord'
import UnlitPass from 'nanogl-pbr/UnlitPass'

import { HexToNewVec3 } from '@/webgl/core/Color'
import { RenderContext } from '@/webgl/core/RenderContext'
import gui from '@/webgl/dev/gui'
import FPSLimiter from '@/webgl/FPSLimiter'
import { mix } from '@/webgl/math'
import GltfResource from '@/webgl/resources/GltfResource'
import { CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import ILoadable from '@/webgl/runner/interfaces/ILoadable'
import { IRenderable } from '@/webgl/runner/interfaces/IRenderable'
import MeshRendererProxy from '@/webgl/runner/rendering/MeshRendererProxy'

// #region GlowChunk
class Glow extends Chunk {
  private uvs: Input
  private reveal: Input
  private color: Input

  public colorUniform: Uniform
  public revealUniform: Uniform
  public alpha: number

  public get Reveal() {
    return this.revealUniform.value[0]
  }
  public set Reveal(v: number) {
    this.revealUniform.value[0] = v
  }

  constructor() {
    super(true, false)
    this.addChild((this.uvs = new Input('TexCoord', 2, ShaderType.FRAGMENT)))
    this.addChild((this.reveal = new Input('Reveal', 1, ShaderType.FRAGMENT)))
    this.addChild((this.color = new Input('Color', 4, ShaderType.FRAGMENT)))
    this.uvs.attachAttribute(TexCoord.create('aTexCoord0').attrib)
    this.revealUniform = this.reveal.attachUniform('uReveal')
    this.colorUniform = this.color.attachUniform('uColor')
  }

  _genCode(slots: ChunksSlots): void {
    slots.add(
      'postf',
      /*glsl*/ `
      FragColor.xyz = Color().xyz;
      float lower = step(TexCoord().y, 0.001);
      float upper = max(smoothstep(1.0, 0.001, TexCoord().y), 0.0) * (1.0 - lower);
      FragColor.a *= lower + upper * Reveal();
      FragColor.a *= FragColor.a;
      FragColor.a *= Color().a;
    `
    )
  }
}
// #endregion

// #region Pastille
class Pastille implements IRenderable, ILoadable {
  PATH = 'common/player.glb'

  node: Node = new Node()
  gltf: Gltf
  tween: gsap.core.Tween
  weights: Float32Array
  time: number = 0

  limiter: FPSLimiter
  index: number = 0
  stressRenderables: MeshRendererProxy[] = []
  calmRenderables: MeshRendererProxy[] = []

  currentShape: MeshRendererProxy
  private stress: number = 0

  constructor(
    readonly gl: GLContext,
    readonly size: number,
    readonly glow: Glow
  ) {
    this.limiter = new FPSLimiter()
    this.limiter.fps = 1
    this.limiter.enabled = true
  }

  async load(abort: AbortSignal): Promise<void> {
    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()

    overrides.overridePass('player', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.inputs.add(this.glow)
      pass.glconfig.enableBlend(true)
      pass.glconfig.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA)
      pass.glconfig.depthMask(false)
      pass.glconfig.enableDepthTest(false)
      return passInstance.pass
    })

    this.gltf = await new GltfResource(this.PATH, this.gl, { extensions: [overrides] }).load(abort)

    for (const n of this.gltf.nodes) {
      if (!n.renderable) continue
      if (n.name == 'calm_4' || n.name == 'stress_3') continue
      const renderer = new MeshRendererProxy(this.gltf, n.renderable.mesh)
      if (n.name.match('calm')) this.calmRenderables.push(renderer)
      if (n.name.match('stress')) this.stressRenderables.push(renderer)
    }

    this.node.add(this.gltf.root)

    this.gltf.root.scale[0] = this.size
    this.gltf.root.scale[1] = this.size
    this.gltf.root.scale[2] = this.size * 3.0
    this.gltf.root.invalidate()
  }

  hide() {
    const o = { scale: 1 }
    this.tween?.kill()
    this.tween = gsap.to(o, {
      scale: 0,
      duration: 0.5,
      onUpdate: () => this.node.setScale(o.scale),
    })
  }

  show() {
    const o = { scale: 0 }
    this.tween?.kill()
    this.tween = gsap.to(o, {
      scale: 1,
      duration: 0.5,
      onUpdate: () => this.node.setScale(o.scale),
    })
  }

  setStress(value: number) {
    this.stress = value
  }

  preRender(dt: number): void {
    this.time += dt
    const lerped = this.stress * this.stress
    this.limiter.fps = mix(1, 5, lerped)
    if (this.limiter.step(dt)) {
      this.index += 1
      const pool = Math.random() < lerped ? this.stressRenderables : this.calmRenderables
      const idx = this.index % pool.length
      this.currentShape = pool[idx]
    }
  }

  render(context: RenderContext): void {
    if (!this.currentShape) return
    this.currentShape.node._wmatrix.set(this.gltf.root._wmatrix)
    this.currentShape.render(context)
  }
}
// #endregion

// #region SpeedChunk
class SpeedChunk extends Chunk {
  private uvs: Input
  private settingsInput: Input

  public settings: Uniform

  public get Time() {
    return this.settings.value[0]
  }
  public set Time(v: number) {
    this.settings.value[0] = v
  }

  public get Reveal() {
    return this.settings.value[1]
  }
  public set Reveal(v: number) {
    this.settings.value[1] = v
  }

  constructor() {
    super(true, false)
    this.addChild((this.uvs = new Input('TexCoord', 2, ShaderType.FRAGMENT)))
    this.addChild((this.settingsInput = new Input('Settings', 2, ShaderType.FRAGMENT)))
    this.uvs.attachAttribute(TexCoord.create('aTexCoord0').attrib)
    this.settings = this.settingsInput.attachUniform('uSpeed', 2)
  }

  _genCode(slots: ChunksSlots): void {
    slots.add(
      'pf',
      /*glsl*/ `
      #define TIME Settings().x
      #define REVEAL Settings().y
      `
    )

    slots.add(
      'postf',
      /*glsl*/ `
      vec2 texcoord = TexCoord();
      texcoord.y = 1.0 - texcoord.y;
      float phase = TIME + texcoord.x * 2.0 + texcoord.y;
      float psin = sin(phase);
      FragColor.a *= step(0.995 + (1.0 - REVEAL) * 0.005, psin);
      if(FragColor.a <= 0.0) discard;
    `
    )
  }
}
// #endregion

// #region SpeedLines
class SpeedLines implements IRenderable, ILoadable {
  PATH = 'common/speedlines.glb'

  node: Node = new Node()
  gltf: Gltf
  chunk: SpeedChunk
  weights: Float32Array = null

  private scrollSpeed: number = 0
  private reveal: number = 0

  constructor(
    readonly gl: GLContext,
    readonly size: number
  ) {
    this.chunk = new SpeedChunk()
  }

  async load(abort: AbortSignal): Promise<void> {
    const overrides: MaterialOverrideExtension = new MaterialOverrideExtension()
    overrides.overridePass('line', (ctx, m) => {
      const passInstance = m.getPass('color')
      const pass = passInstance.pass as UnlitPass
      pass.inputs.add(this.chunk)
      return passInstance.pass
    })
    this.gltf = await new GltfResource(this.PATH, this.gl, { extensions: [overrides] }).load(abort)
    this.gltf.root.setScale(this.size)
    this.gltf.root.scale[2] = this.size * 3.0
    this.weights = this.gltf.renderables[0].node.weights
    this.node.add(this.gltf.root)
  }

  setStress(value: number) {
    if (this.weights) this.weights[0] = value
  }

  setScrollSpeed(value: number, maxvalue: number) {
    this.reveal = mix(0, maxvalue, value) / maxvalue
    this.scrollSpeed = value
  }

  preRender(dt: number): void {
    this.chunk.Reveal = this.reveal
    this.chunk.Time += dt * this.scrollSpeed
  }

  render(context: RenderContext) {
    for (const renderable of this.gltf.renderables) {
      renderable.render(context.gl, context.camera, context.mask, context.pass, context.glConfig)
    }
  }
}
// #endregion

// #region PlayerRenderer
export default class PlayerRenderer implements ILoadable {
  node: Node
  // gltf: Gltf;

  glow: Glow

  idleColor: vec4 = vec4.fromValues(1, 1, 1, 1.0)
  goodColor: vec4 = vec4.fromValues(1, 1, 1, 1.0)
  badColor: vec4 = vec4.fromValues(1, 1, 1, 1.0)

  grabbing: boolean = false
  grabDisabled: boolean = false
  glowTween: gsap.core.Timeline

  pastille: Pastille
  lines: SpeedLines

  stress: number = 0

  constructor(
    readonly gl: GLContext,
    readonly world: GameWorld
  ) {
    this.node = new Node()
    this.world.root.add(this.node)
    this.glow = new Glow()
    this.glow.colorUniform.set(...this.idleColor)

    const LANE_SIZE = this.world.settings.LANE_SIZE

    this.pastille = new Pastille(gl, LANE_SIZE, this.glow)
    this.lines = new SpeedLines(gl, LANE_SIZE)

    const fld = gui.folder('Player')
    fld.add(this.glow, 'Reveal', { min: 0, max: 1 })
    fld.btn('Glow Succeed', () => this.triggerGlow(CollectStatus.Succeed))
    fld.btn('Glow Failed', () => this.triggerGlow(CollectStatus.Failed))
  }

  setColors(idle: number, good: number, bad: number) {
    this.idleColor.set(HexToNewVec3(idle))
    this.goodColor.set(HexToNewVec3(good))
    this.badColor.set(HexToNewVec3(bad))
    this.glow.colorUniform.set(...this.idleColor)
  }

  async load(abort: AbortSignal): Promise<void> {
    await Promise.all([this.pastille.load(abort)])

    await Promise.all([this.pastille.load(abort), this.lines.load(abort)])

    this.node.add(this.pastille.node)
    this.node.add(this.lines.node)
  }

  triggerGlow(status: CollectStatus) {
    this.world.state.setCollectStatus(status)

    this.grabbing = true
    const dst = status == CollectStatus.Succeed ? this.goodColor : this.badColor

    this.glowTween?.kill()

    const src = { r: dst[0], g: dst[1], b: dst[2], a: dst[3] }
    this.glow.colorUniform.set(dst[0], dst[1], dst[2], dst[3])

    this.glowTween = gsap
      .timeline({
        onComplete: () => {
          this.grabbing = false
          this.world.state.setCollectStatus(CollectStatus.Neutral)
        },
      })
      .to(this.glow, { Reveal: 1.0, duration: 0.05, ease: 'none' })
      .to(this.glow, { Reveal: 0, duration: 0.5, delay: 0.15, ease: 'none' })
      .to(
        src,
        {
          r: this.idleColor[0],
          g: this.idleColor[1],
          b: this.idleColor[2],
          duration: 0.5,
          ease: 'none',
          onUpdate: () => this.glow.colorUniform.set(src.r, src.g, src.b, src.a),
        },
        '-=0.15'
      )
  }

  preRender(time: TimeEntity): void {
    const settings = this.world.settings
    const player = this.world.player
    this.node.z = settings.GRAB_THRESHOLD

    this.stress = mix(this.stress, this.world.state.value.jauge, time.deltaTime * 2.0)

    this.pastille.setStress(this.stress)
    this.lines.setStress(this.stress)
    this.lines.setScrollSpeed(settings.scrollSpeed, settings.maxSpeed)

    this.pastille.preRender(time.unscaledDeltaTime)
    this.lines.preRender(time.unscaledDeltaTime)

    const lx = player.node.x
    const tx = player.lane * settings.LANE_SIZE
    const dx = Math.min(Math.abs(lx - tx) / settings.LANE_SIZE, 1.0)

    const activeLane = dx > 0.5 ? player.visualLane : player.lane
    this.node.x = activeLane * settings.LANE_SIZE
    this.glow.colorUniform.value[3] = 1.0 - Math.sin(dx * Math.PI)

    if (player.grabDisabled != this.grabDisabled) {
      this.grabDisabled = player.grabDisabled

      if (this.grabDisabled) {
        this.pastille.hide()
      } else {
        this.pastille.show()
      }
    }

    const collectibles = this.world.collectibles
    for (const collectible of collectibles) {
      if (!collectible.collected || collectible.releasing) continue
      this.triggerGlow(collectible.collectStatus)
    }
  }

  render(context: RenderContext): void {
    this.pastille.render(context)
    this.lines.render(context)
  }
}
// #endregion
